import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Music, Instagram, Youtube, ExternalLink, Mail } from 'lucide-react';
import api from '../services/api';

const SocialLinks = ({ social }) => (
  <div className="flex flex-wrap gap-3">
    <Button
      variant="outline"
      className="border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a1128] transition-all duration-300"
      onClick={() => window.open(social?.spotify || '#', '_blank')}
    >
      <ExternalLink className="w-4 h-4 mr-2" />
      Spotify
    </Button>
    <Button
      variant="outline"
      className="border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a1128] transition-all duration-300"
      onClick={() => window.open(social?.youtube || '#', '_blank')}
    >
      <Youtube className="w-4 h-4 mr-2" />
      YouTube
    </Button>
    <Button
      variant="outline"
      className="border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a1128] transition-all duration-300"
      onClick={() => window.open(social?.instagram || '#', '_blank')}
    >
      <Instagram className="w-4 h-4 mr-2" />
      Instagram
    </Button>
    <Button
      variant="outline"
      className="border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a1128] transition-all duration-300"
      onClick={() => window.open(social?.soundcloud || '#', '_blank')}
    >
      <ExternalLink className="w-4 h-4 mr-2" />
      SoundCloud
    </Button>
  </div>
);

const FooterLinks = () => {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
        Quick Links
      </h3>
      <ul className="space-y-2">
        {[
          { id: 'home', label: 'Home' },
          { id: 'discography', label: 'Discography' },
          { id: 'lyrics', label: 'Lyrics' }
        ].map(({ id, label }) => (
          <li key={id}>
            <button
              onClick={() => scrollTo(id)}
              className="text-gray-400 hover:text-[#d4af37] transition-colors duration-300"
            >
              {label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Footer = () => {
  const [artistInfo, setArtistInfo] = useState(null);

  const fetchArtistInfo = useCallback(async () => {
    try {
      const response = await api.getArtistInfo();
      setArtistInfo(response.data);
    } catch (err) {
      console.error('Error fetching artist info:', err);
    }
  }, []);

  useEffect(() => {
    fetchArtistInfo();
  }, [fetchArtistInfo]);

  return (
    <footer id="contact" className="bg-gradient-to-b from-[#0a1128] to-[#000000] border-t border-[#d4af37]/20 pb-24">
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Music className="w-8 h-8 text-[#d4af37]" />
              <span
                className="text-2xl font-bold text-white"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {artistInfo?.name || 'VjkumarVarma'}
              </span>
            </div>
            <p className="text-gray-400 leading-relaxed">
              {artistInfo?.bio || ''}
            </p>
          </div>

          <FooterLinks />

          {/* Social & Contact */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              Connect
            </h3>
            <p className="text-gray-400 mb-4">
              Follow me on social media and streaming platforms
            </p>
            <SocialLinks social={artistInfo?.social} />
            <div className="pt-2">
              <Button
                variant="outline"
                className="border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a1128] transition-all duration-300"
                onClick={() => window.location.href = `mailto:${artistInfo?.email || 'vjkumarvarma@gmail.com'}`}
              >
                <Mail className="w-4 h-4 mr-2" />
                {artistInfo?.email || 'vjkumarvarma@gmail.com'}
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-[#d4af37]/20">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} VjkumarVarma. All rights reserved.
            </p>
            <p className="text-gray-500 text-sm">
              Crafted with passion and dedication
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
